/**
 * Routine の締めの確定(routine_closings)の読み込み(2026-10-23)。読むだけで書き込みはしない
 * (書くのは受け口(サーバー)とテスト環境の投入スクリプトだけ。権限ルールで画面からは書けない)。
 */
import { collection, doc, documentId, getDoc, getDocs, query, where } from 'firebase/firestore';
import { getDb } from '@core/lib/firebase';
import { fetchInvoicesForMonth } from '@core/receivables/invoiceRepo';
import type { Invoice, MonthStr, RoutineClosing, Transaction } from '@core/lib/types';

export async function fetchRoutineClosings(months: MonthStr[]): Promise<RoutineClosing[]> {
  if (months.length === 0) return [];
  const snap = await getDocs(query(collection(getDb(), 'routine_closings'), where('month', 'in', months.slice(0, 10))));
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<RoutineClosing, 'id'>) }));
}

/** 取引を ID で読む(30件ずつ) */
export async function fetchTransactionsByIds(ids: string[]): Promise<Transaction[]> {
  const unique = [...new Set(ids)];
  const chunks: string[][] = [];
  for (let i = 0; i < unique.length; i += 30) chunks.push(unique.slice(i, i + 30));
  const lists = await Promise.all(
    chunks.map(async (chunk) => {
      const snap = await getDocs(query(collection(getDb(), 'transactions'), where(documentId(), 'in', chunk)));
      return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Transaction, 'id'>) }) as Transaction);
    }),
  );
  return lists.flat();
}

/** その月の売上の請求書(①の確定の請求の状態を出すため) */
export async function fetchInvoicesOfMonth(month: MonthStr): Promise<Invoice[]> {
  return fetchInvoicesForMonth(month);
}

/** 1件(印刷ページ用) */
export async function fetchRoutineClosing(id: string): Promise<RoutineClosing | null> {
  const snap = await getDoc(doc(getDb(), 'routine_closings', id));
  return snap.exists() ? ({ id: snap.id, ...(snap.data() as Omit<RoutineClosing, 'id'>) } as RoutineClosing) : null;
}

/** 支払いの取引(external.source_id)から、その確定の写しを探す。取り消し済みでない一番新しい版 */
export async function fetchRoutineClosingBySource(sourceId: string): Promise<RoutineClosing | null> {
  const snap = await getDocs(query(collection(getDb(), 'routine_closings'), where('source_id', '==', sourceId)));
  const list = snap.docs
    .map((d) => ({ id: d.id, ...(d.data() as Omit<RoutineClosing, 'id'>) }) as RoutineClosing)
    .filter((c) => c.status !== 'cancelled')
    .sort((a, b) => (a.confirmed_at < b.confirmed_at ? 1 : -1));
  return list[0] ?? null;
}
